import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import type { IClient } from '@interfaces/client';
import type { IFiscalCalculation } from '@interfaces/fiscal';
import type { IProduct } from '@interfaces/product';
import {
	ESellerOrderStatus,
	ESellerOrderStep,
	type ISellerOrder,
	type ISellerOrderAddItemOptions,
	type ISellerOrderChecklist,
	type ISellerOrderItem,
	type ISellerOrderDraft,
	type ISellerOrderPayment,
	type ISellerOrderShipment,
} from '@interfaces/seller-order';
import { mergeTaxesIntoLines } from '@utils/cart-fiscal.util';

export interface ISellerOrderState {
	currentStep: ESellerOrderStep;
	order: ISellerOrder;
	drafts: ISellerOrderDraft[];
	draftSavedAt: Date | null;
	isSaving: boolean;
	isSubmitting: boolean;
	checklist: ISellerOrderChecklist;
}

const EMPTY_ORDER: ISellerOrder = {
	items: [],
	shipment: {},
	payment: {},
	subtotal: 0,
	totalDiscount: 0,
	total: 0,
	status: ESellerOrderStatus.DRAFT,
};

export const INITIAL_STATE: ISellerOrderState = {
	currentStep: ESellerOrderStep.BUILD,
	order: { ...EMPTY_ORDER },
	drafts: [],
	draftSavedAt: null,
	isSaving: false,
	isSubmitting: false,
	checklist: {
		hasClient: false,
		hasItems: false,
		hasPaymentCondition: false,
		hasShippingMethod: false,
	},
};

@Injectable({
	providedIn: 'root',
})
export class SellerOrderStore {
	/** AFV allowBypassMinimumPrice: the unit can stay under the list minimum. */
	private ignoreMinimumPrice = false;
	private readonly _state = new BehaviorSubject<ISellerOrderState>({
		...INITIAL_STATE,
		order: { ...EMPTY_ORDER, items: [], shipment: {}, payment: {} },
	});
	readonly state$ = this._state.asObservable();

	get snapshot(): ISellerOrderState {
		return this._state.value;
	}

	setStep(step: ESellerOrderStep) {
		this.patch({ currentStep: step });
	}

	setClient(client: IClient | null) {
		// Never keep previous client's payment/shipping — avoids "sujeira" on Trocar cliente.
		const order = {
			...this.snapshot.order,
			clientId: client?.id,
			client: client ?? undefined,
			priceListId: client?.priceListId
				? Number(client.priceListId)
				: this.snapshot.order.priceListId,
			payment: {
				...this.snapshot.order.payment,
				paymentMethodId: client?.paymentMethodId,
				paymentMethod: undefined,
				paymentTermId: client?.paymentTermId,
				paymentTerm: undefined,
			},
			shipment: {
				...this.snapshot.order.shipment,
				shippingMethodId: client?.shippingMethodId,
				shippingTypeId: client?.shippingTypeId,
			},
		};
		this.patchOrder(order);
	}

	setPriceList(priceListId: number | undefined, priceList?: string) {
		const items = this.snapshot.order.items.map(item =>
			this.applyListedPrice(item, priceListId, priceList)
		);
		this.patchOrder({
			...this.snapshot.order,
			priceListId,
			priceList,
			items,
		});
	}

	setItemPriceList(
		productId: number,
		priceListId: number | undefined,
		priceList?: string,
		packingId?: number | null
	) {
		const items = this.snapshot.order.items.map(item => {
			if (item.productId !== productId) return item;
			if (
				packingId !== undefined &&
				(item.packingId ?? null) !== (packingId ?? null)
			) {
				return item;
			}
			return this.applyListedPrice(item, priceListId, priceList);
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	setStore(storeId: number | undefined, store?: string, storehouseId?: number) {
		const resolvedStorehouseId = storehouseId ?? storeId;
		const items = this.snapshot.order.items.map(item => ({
			...item,
			storeId,
			storehouseId: resolvedStorehouseId,
		}));
		this.patchOrder({
			...this.snapshot.order,
			storeId,
			store,
			storehouseId: resolvedStorehouseId,
			items,
		});
	}

	setOrderType(
		orderTypeId: number | undefined,
		orderType?: string,
		generatesFlexBalance = false,
		flags?: { isDevolution?: boolean; isQuote?: boolean }
	) {
		const items = this.snapshot.order.items.map(item =>
			this.priceItem(item, generatesFlexBalance)
		);
		this.patchOrder({
			...this.snapshot.order,
			generatesFlexBalance,
			isDevolution: !!flags?.isDevolution,
			isQuote: !!flags?.isQuote,
			payment: {
				...this.snapshot.order.payment,
				orderTypeId,
				orderType,
			},
			items,
		});
	}

	setIgnoreMinimumPrice(ignore: boolean) {
		if (this.ignoreMinimumPrice === ignore) return;
		this.ignoreMinimumPrice = ignore;
		const items = this.snapshot.order.items.map(item => this.priceItem(item));
		this.patchOrder({ ...this.snapshot.order, items });
	}

	setExpiresAt(expiresAt: string) {
		this.patchOrder({ ...this.snapshot.order, expiresAt });
	}

	/** Quantity 0 becomes 1 when the order is saved. */
	ensureMinimumQuantities() {
		const items = this.snapshot.order.items.map(item =>
			item.quantity >= 1 ? item : this.priceItem({ ...item, quantity: 1 })
		);
		this.patchOrder({ ...this.snapshot.order, items });
	}

	/** AFV setShipmentPrice: split the informed freight by item quantity. */
	distributeShipmentPrice(shipmentPrice: number) {
		if (shipmentPrice < 0) return;
		const items = [...this.snapshot.order.items];
		const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
		const pricePerUnit = totalQuantity > 0 ? shipmentPrice / totalQuantity : 0;
		let remaining = shipmentPrice;
		const next = items.map((item, index) => {
			const isLast = index === items.length - 1;
			const share = isLast
				? remaining
				: Math.round(pricePerUnit * item.quantity * 100) / 100;
			remaining -= share;
			return this.priceItem({ ...item, shipmentPrice: Math.max(0, share) });
		});
		this.patchOrder({
			...this.snapshot.order,
			shipmentPrice,
			items: next,
		});
	}

	/** AFV: a percentage outside 0–100 is ignored. */
	applyOrderDiscountPercentage(discountPercentage: number) {
		if (discountPercentage < 0 || discountPercentage > 100) return;
		const items = this.snapshot.order.items.map(item =>
			this.priceItem({ ...item, discountPercentage })
		);
		const base = items.reduce(
			(sum, item) => sum + item.unitPrice * item.quantity,
			0
		);
		this.patchOrder({
			...this.snapshot.order,
			discountPercentage,
			discount: (base * discountPercentage) / 100,
			items,
		});
	}

	/** AFV: negative values are ignored; values above merchandise total are capped. */
	applyOrderDiscountValue(discount: number) {
		const base = this.snapshot.order.items.reduce(
			(sum, item) => sum + (item.unitPrice || 0) * item.quantity,
			0
		);
		if (discount < 0) return;
		if (base <= 0) {
			if (discount > 0) return;
			this.applyOrderDiscountPercentage(0);
			return;
		}
		const capped = Math.min(discount, base);
		const discountPercentage = (100 * capped) / base;
		this.applyOrderDiscountPercentage(discountPercentage);
	}

	replaceItemProduct(
		productId: number,
		product: IProduct,
		packingId?: number | null
	) {
		const items = this.snapshot.order.items.map(item => {
			if (item.productId !== productId) return item;
			if (
				packingId !== undefined &&
				(item.packingId ?? null) !== (packingId ?? null)
			) {
				return item;
			}
			return { ...item, product };
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	addItem(product: IProduct, options: ISellerOrderAddItemOptions = {}) {
		const quantity = options.quantity ?? 1;
		const packingId = options.packingId ?? null;
		const items = [...this.snapshot.order.items];
		const existing = items.find(
			i => i.productId === product.id && (i.packingId ?? null) === packingId
		);

		const priceListId = options.priceListId ?? this.snapshot.order.priceListId;
		const listed = this.resolveListedPrice(product, priceListId, packingId);
		const originalPrice = options.originalPrice ?? listed.price;
		const minimumPrice = options.minimumPrice ?? listed.minimumPrice;
		const requestedPrice = options.unitPrice ?? originalPrice;
		const unitPrice = this.ignoreMinimumPrice
			? Math.max(requestedPrice || 0, 0)
			: Math.max(requestedPrice || 0, minimumPrice);

		const order = this.snapshot.order;
		const orderDiscountPercentage = Math.max(
			0,
			Math.min(100, order.discountPercentage || 0)
		);
		if (existing) {
			existing.quantity += quantity;
			existing.storeId = order.storeId ?? existing.storeId;
			existing.storehouseId = order.storehouseId ?? existing.storehouseId;
			Object.assign(existing, this.priceItem(existing));
		} else {
			items.push(
				this.priceItem({
					productId: product.id,
					product,
					quantity,
					sellingUnit: product.sellingUnit,
					originalPrice,
					unitPrice,
					discount: 0,
					discountPercentage: orderDiscountPercentage,
					unitPriceFinal: unitPrice,
					minimumPrice,
					shipmentPrice: options.shipmentPrice ?? 0,
					flexBalance: 0,
					priceListId,
					priceList: options.priceList ?? order.priceList,
					storeId: order.storeId,
					storehouseId: order.storehouseId,
					packingId,
					packingQuantity: options.packingQuantity ?? null,
					packingLabel: options.packingLabel,
					variationLabel: options.variationLabel,
					subtotal: 0,
				})
			);
		}

		this.patchOrder({ ...this.snapshot.order, items });
	}

	updateItemQuantity(
		productId: number,
		quantity: number,
		packingId?: number | null
	) {
		const items = this.snapshot.order.items.map(i => {
			const samePacking =
				packingId === undefined ||
				(i.packingId ?? null) === (packingId ?? null);
			if (i.productId !== productId || !samePacking) return i;
			return this.priceItem({ ...i, quantity });
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	updateItemPrice(productId: number, unitPrice: number) {
		const items = this.snapshot.order.items.map(i => {
			if (i.productId !== productId) return i;
			return this.priceItem({ ...i, unitPrice: unitPrice || 0 });
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	updateItemDiscount(productId: number, discountPercentage: number) {
		const items = this.snapshot.order.items.map(i => {
			if (i.productId !== productId) return i;
			const pct = Math.max(0, Math.min(100, discountPercentage || 0));
			return this.priceItem({ ...i, discountPercentage: pct });
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	updateItemShipmentPrice(
		productId: number,
		shipmentPrice: number,
		packingId?: number | null
	) {
		const items = this.snapshot.order.items.map(i => {
			const samePacking =
				packingId === undefined ||
				(i.packingId ?? null) === (packingId ?? null);
			if (i.productId !== productId || !samePacking) return i;
			return this.priceItem({
				...i,
				shipmentPrice: Math.max(0, shipmentPrice || 0),
			});
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	applyItemTaxes(calculation: IFiscalCalculation | null) {
		const items = mergeTaxesIntoLines(this.snapshot.order.items, calculation);
		this.patchOrder({ ...this.snapshot.order, items });
	}

	removeItem(productId: number, packingId?: number | null) {
		const items = this.snapshot.order.items.filter(i => {
			if (i.productId !== productId) return true;
			if (packingId === undefined) return false;
			return (i.packingId ?? null) !== (packingId ?? null);
		});
		this.patchOrder({ ...this.snapshot.order, items });
	}

	updateShipment(shipment: Partial<ISellerOrderShipment>) {
		this.patchOrder({
			...this.snapshot.order,
			shipment: { ...this.snapshot.order.shipment, ...shipment },
		});
	}

	updatePayment(payment: Partial<ISellerOrderPayment>) {
		this.patchOrder({
			...this.snapshot.order,
			payment: { ...this.snapshot.order.payment, ...payment },
		});
	}

	setDrafts(drafts: ISellerOrderDraft[]) {
		this.patch({ drafts });
	}

	setDraftSaved(date: Date | null) {
		this.patch({ draftSavedAt: date, isSaving: false });
	}

	setSaving(isSaving: boolean) {
		this.patch({ isSaving });
	}

	setSubmitting(isSubmitting: boolean) {
		this.patch({ isSubmitting });
	}

	loadOrder(order: ISellerOrder) {
		this.patchOrder(order);
	}

	reset() {
		this._state.next({
			...INITIAL_STATE,
			order: { ...EMPTY_ORDER, items: [], shipment: {}, payment: {} },
			drafts: this.snapshot.drafts,
		});
	}

	/** AFV: discount is a % of unitPrice; final unit is what the client pays. */
	private priceItem(
		item: ISellerOrderItem,
		generatesFlex = !!this.snapshot.order.generatesFlexBalance
	): ISellerOrderItem {
		const requested = Math.max(item.unitPrice || 0, 0);
		const unitPrice = this.ignoreMinimumPrice
			? requested
			: Math.max(requested, item.minimumPrice || 0);
		const discountPercentage = Math.max(
			0,
			Math.min(100, item.discountPercentage || 0)
		);
		const discount = (unitPrice * discountPercentage) / 100;
		const unitPriceFinal = unitPrice - discount;
		const shipmentPrice = item.shipmentPrice || 0;
		return {
			...item,
			unitPrice,
			discountPercentage,
			discount,
			unitPriceFinal,
			shipmentPrice,
			flexBalance: generatesFlex
				? (unitPriceFinal - (item.originalPrice || 0)) * item.quantity
				: 0,
			subtotal: unitPriceFinal * item.quantity + shipmentPrice,
		};
	}

	/** AFV getPrice: a chosen list with no row on the product is 0, not the first price. */
	private resolveListedPrice(
		product: IProduct,
		priceListId?: number,
		packingId?: number | null
	): { price: number; minimumPrice: number } {
		const packing = packingId
			? product.packings?.find(p => p.id === packingId)
			: undefined;
		const prices = packing?.prices?.length
			? packing.prices
			: product.prices || [];
		if (priceListId == null) {
			const first = prices[0];
			return {
				price: first?.price ?? product.originalPrice ?? 0,
				minimumPrice: first?.minimumPrice ?? 0,
			};
		}
		const listId = Number(priceListId);
		const match = prices.find(p => Number(p.priceListId) === listId);
		return {
			price: match?.price ?? 0,
			minimumPrice: match?.minimumPrice ?? 0,
		};
	}

	private applyListedPrice(
		item: ISellerOrderItem,
		priceListId: number | undefined,
		priceList?: string
	): ISellerOrderItem {
		const listed = this.resolveListedPrice(
			item.product,
			priceListId,
			item.packingId
		);
		return this.priceItem({
			...item,
			priceListId,
			priceList: priceList ?? item.priceList,
			originalPrice: listed.price,
			minimumPrice: listed.minimumPrice,
			unitPrice: listed.price,
		});
	}

	private patchOrder(order: ISellerOrder) {
		const subtotal = order.items.reduce((sum, i) => sum + i.subtotal, 0);
		const totalDiscount = order.items.reduce(
			(sum, i) => sum + i.discount * i.quantity,
			0
		);

		const updatedOrder: ISellerOrder = {
			...order,
			subtotal,
			totalDiscount,
			// Keep Desconto R$ in sync with the real item discounts.
			discount: totalDiscount,
			total: subtotal,
		};

		const checklist: ISellerOrderChecklist = {
			hasClient: Boolean(updatedOrder.clientId),
			hasItems: updatedOrder.items.length > 0,
			hasPaymentCondition: Boolean(updatedOrder.payment?.paymentTermId),
			hasShippingMethod: Boolean(updatedOrder.shipment?.shippingMethodId),
		};

		this.patch({ order: updatedOrder, checklist });
	}

	private patch(partial: Partial<ISellerOrderState>) {
		this._state.next({ ...this.snapshot, ...partial });
	}
}
